import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, X, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { StaxMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const openContact = (preset = "") =>
  window.dispatchEvent(new CustomEvent("stax:contact", { detail: preset }));

export const ContactDialog = () => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("");
  const [form, setForm] = useState({ name: "", email: "", business: "", message: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setPreset(typeof e.detail === "string" ? e.detail : "");
      setOpen(true);
    };
    window.addEventListener("stax:contact", handler);
    return () => window.removeEventListener("stax:contact", handler);
  }, []);

  useEffect(() => {
    if (preset) setForm((f) => ({ ...f, message: preset }));
  }, [preset]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File too large — 25MB max.");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("business", form.business);
      fd.append("message", form.message);
      fd.append("source", "contact-dialog");
      if (file) fd.append("file", file);
      await axios.post(`${API}/contact`, fd);
      toast.success("Message sent! STAX will be in touch soon.");
      setForm({ name: "", email: "", business: "", message: "" });
      setFile(null);
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full border-2 border-ink bg-white px-4 py-3 font-body text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta placeholder:text-ink/40";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="contact-dialog"
        data-lenis-prevent
        className="border-4 border-ink bg-paper p-0 sm:max-w-lg rounded-none shadow-hard gap-0 [&>button]:hidden"
      >
        <div className="flex items-center justify-between border-b-4 border-ink bg-staxyellow px-6 py-4">
          <div className="flex items-center gap-3">
            <StaxMark size={22} />
            <DialogTitle className="font-display uppercase text-xl text-ink">Start a Project</DialogTitle>
          </div>
          <button data-testid="contact-close-button" onClick={() => setOpen(false)} aria-label="Close"
            className="border-2 border-ink bg-white p-1 hover:bg-staxmagenta hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="contact-name-input" className={field} placeholder="Your name *"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="contact-email-input" className={field} type="email" placeholder="Email *"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <input data-testid="contact-business-input" className={field} placeholder="Business / brand name"
            value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} />
          <textarea data-testid="contact-message-input" className={`${field} min-h-[110px] resize-none`}
            placeholder="Tell me about your project..."
            value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <label
            data-testid="contact-file-label"
            className="flex cursor-pointer items-center gap-3 border-2 border-dashed border-ink/40 bg-white px-4 py-3 text-sm font-medium text-ink/60 transition-colors hover:border-staxmagenta"
          >
            <Paperclip size={16} className="shrink-0 text-staxmagenta" />
            <span className="flex-1 truncate">
              {file ? file.name : "Attach a brief, logo, or doc (optional, 25MB max)"}
            </span>
            {file && (
              <button
                type="button"
                data-testid="contact-file-clear"
                aria-label="Remove attachment"
                onClick={(e) => { e.preventDefault(); setFile(null); }}
                className="shrink-0 border border-ink p-0.5 hover:bg-staxmagenta hover:text-white"
              >
                <X size={12} />
              </button>
            )}
            <input
              data-testid="contact-file-input"
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.txt,.zip,.csv"
              onChange={pickFile}
            />
          </label>
          <button data-testid="contact-submit-button" type="submit" disabled={sending}
            className="group flex w-full items-center justify-center gap-2 border-2 border-ink bg-staxmagenta px-6 py-4 font-display uppercase text-lg text-white shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {sending ? "Sending..." : "Send It"} <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
